import _ from "lodash";
import PropTypes from "prop-types";
import React, { useState, useContext, useEffect, useCallback } from "react";
import { createUseStyles } from "react-jss";
import { StyleTypeContext, TranslationContext } from "../../umbilical_tools";
import { useHistory, withRouter } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import FullScreenLoader from "../../components/widgets/FullScreenLoader";
import {
  activitySearchFormUpdate,
  selectedActivityLoading,
  selectedActivityReset,
  singleActivitySelected,
} from "../../actions/search/activities_search";
import Description from "../../components/common/activity/Description";
import { ActPriceSearchForm } from "../../components/forms/ActivityPriceSearchForm";
import Info from "../../components/common/activity/Info";
import { Location, Rating } from "../Activities/Activity/helpers";
import { retrieveSearchSessionMetadata } from "../../api";
import { getAdditionalInfo, ImageCarousel, ReviewsDetails } from "./helpers";
import InclusionsExclusions from "../../components/common/activity/InclusionsExclusions";
import { GlobalModalsContext, NoScrollContext } from "../../context";
import { SchedulesModal } from "../../components/forms/ActivityPriceSearchForm/helpers";
import { toast } from "react-toastify";
import { activitiesErrorMsgs } from "../../errors";
import { sendPixelEvent } from "../../helpers";
import { Button } from "../../components/common/Button";
import jssVariables from "../../jss_setup";
import sanitizeHtml from "sanitize-html";

const activityDetailsStyles = createUseStyles({
  activityDetails: (props) => ({
    position: "relative",
    height: props.loading ? "100vh" : "auto",
    paddingTop: jssVariables.navbar_height,
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.activityDetails", {}),
  }),
  header: (props) => ({
    padding: `calc(${jssVariables.normal_gap} * 2) 5vw`,
    ..._.get(props, "customStyles.header", {}),
  }),
  controlBtnsContr: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.controlBtnsContr", {}),
  }),
  basicContent: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} * 2)`,
    padding: `calc(${jssVariables.normal_gap} * 2) 5vw`,
    ..._.get(props, "customStyles.content", {}),
  }),
  title: (props) => ({
    fontSize: "x-large",
    fontWeight: "bold",
    ..._.get(props, "customStyles.title", {}),
  }),
  videoContainer: (props) => ({
    ..._.get(props, "customStyles.videoContainer", {}),
  }),
  bookBtnContainer: (props) => ({
    position: "fixed",
    bottom: 0,
    left: 0,
    zIndex: 0,
    width: "100vw",
    padding: [jssVariables.normal_gap, jssVariables.double_gap],
    background: jssVariables.colors.background.light,
    display: "grid",
    justifyItems: "center",
    ..._.get(props, "customStyles.bookBtnContainer", {}),
  }),
  mobile: {
    display: "block",
  },
  desktop: {
    display: "none",
  },
  [`@media ${jssVariables.media.smallscreen}`]: {
    header: (props) => ({
      padding: "0 25vw 0 15vw",
      paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
      ..._.get(props, "customStyles.header.smallscreen", {}),
    }),
    controlBtnsContr: (props) => ({
      gridTemplateColumns: "max-content max-content",
      gridGap: jssVariables.normal_gap,
      ..._.get(props, "customStyles.controlBtnsContr.smallscreen", {}),
    }),
    basicContent: (props) => ({
      padding: "0 25vw 0 15vw",
      paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
    videoContainer: (props) => ({
      ..._.get(props, "customStyles.videoContainer.smallscreen", {}),
    }),
    bookBtnContainer: (props) => ({
      display: "none",
      ..._.get(props, "customStyles.bookBtnContainer.smallscreen", {}),
    }),
    mobile: {
      display: "none",
    },
    desktop: {
      display: "block",
    },
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    header: (props) => ({
      padding: jssVariables.spacing.center_padding_large,
      paddingTop: jssVariables.double_gap,
      ..._.get(props, "customStyles.header.bigscreen", {}),
    }),
    basicContent: (props) => ({
      padding: jssVariables.spacing.center_padding_large,
      paddingTop: jssVariables.double_gap,
      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
  },
});
var ActivityDetails = ({ page_session_id }) => {
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [bookingData, setBookingData] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState([]);
  const [pickUpAndDates, setPickUpAndDates] = useState({
    dates: [],
    pickup_point: "",
  });

  const dispatch = useDispatch();
  const history = useHistory();

  const { t } = useContext(TranslationContext);
  const { toggleNoScroll } = useContext(NoScrollContext);
  const { setShowActFormModal } = useContext(GlobalModalsContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const { loading, activity, failed } = useSelector((state) => {
    return {
      loading: _.get(state.ActivitySelected, "loading", true),
      activity: _.get(state.ActivitySelected, "activity", {}),
      failed: _.get(state.ActivitySelected, "failed", false),
    };
  });

  const customStyles = cStyler({
    type,
    brand,
    objName: "activityDetailsStyles",
  });

  const classes = activityDetailsStyles({
    customStyles,
    hasHighlights: _.get(activity, "highlights.length", 0) > 0,
  });

  const activity_uid = new URLSearchParams(history.location.search).get(
    "activity_uid"
  );

  const getDetails = useCallback(
    (activity_uid) => dispatch(singleActivitySelected({ activity_uid })),
    [dispatch]
  );

  const memoizedShowActivityOnMap = useCallback(() => {
    if (
      typeof setViewMapMode !== "function" ||
      typeof setSingleActivityForMap !== "function"
    ) {
      return;
    }
  }, [activity_uid]);

  const onGoBack = useCallback(() => {
    dispatch(selectedActivityReset());
    history.push(`/activities/${page_session_id}`);
  }, [dispatch]);

  // TODO: Extract this into hook
  const pageSessionFetcher = useCallback(() => {
    retrieveSearchSessionMetadata({ page_session_id })
      .then((data) => {
        const form = _.get(data, "session_data.data.search_form", {});
        form.search_session_id = _.get(
          data,
          "session_data.data.search_session_id"
        );
        dispatch(activitySearchFormUpdate({ form }));
        getDetails(activity_uid);
      })
      .catch((error) => {
        console.error(error);
        toast.warning(activitiesErrorMsgs.general.no_session, {
          duration: 3000,
        });
        onGoBack();
      });
  }, [dispatch]);

  function onSetShowForm(input) {
    setShowForm(input);
    toggleNoScroll(input);
  }

  function onSetShowModal(show) {
    // show is boolean
    setModalLoading(show);
    setShowModal(show);
    toggleNoScroll(show);
  }

  // Fetch page session data
  useEffect(() => {
    if (!_.isEmpty(activity) && activity_uid === activity.uid) {
      return;
    }

    if (!_.isEmpty(activity) && activity_uid !== activity.uid) {
      dispatch(selectedActivityLoading());
      pageSessionFetcher();
      return;
    }

    if (_.isEmpty(activity)) {
      pageSessionFetcher();
      return;
    }
  }, []);

  useEffect(() => {
    if (!_.isEmpty(activity)) {
      setAdditionalInfo(getAdditionalInfo({ t, activity }));

      return;
    }
  }, [activity]);

  // If fetching details failed, go back
  useEffect(() => {
    if (failed) {
      onGoBack();
    }
  }, [failed]);

  // If activity is not available, go back
  useEffect(() => {
    if (
      (!loading && _.isEmpty(activity)) ||
      (!loading &&
        activity.pickup_point_required &&
        !activity.pickup_points.length) ||
      (!loading &&
        !activity.pickup_point_required &&
        !activity.availability.length) ||
      (!loading &&
        !activity.pickup_points.length &&
        !activity.availability.length)
    ) {
      onGoBack();
      toast.warning(t("notAvailableActivity"), { duration: 3000 });
    }
  }, [activity]);

  // Send Facebook pixel event
  useEffect(() => {
    sendPixelEvent("ViewActivityDetails");
  }, []);

  const LocationComp = (
    <Location
      location={_.get(activity, "location", "")}
      distanceFromSelection={_.get(
        activity,
        "computed_data.distanceFromSelection",
        0
      )}
      customStyles={customStyles}
      showActivityOnMap={memoizedShowActivityOnMap}
    />
  );

  const RatingComp = <Rating rating={_.get(activity, "ratings", {})} />;

  return (
    <div className={classes.activityDetails}>
      {loading || _.isEmpty(activity) ? (
        <FullScreenLoader />
      ) : (
        <React.Fragment>
          <ImageCarousel
            showRating={false}
            activity={activity}
            tagColor={""}
            isMarketplace={false}
            customStyles={customStyles}
          />
          <div className={classes.header}>
            {LocationComp}
            <div className={classes.title}>{_.get(activity, "title", "")}</div>
            {RatingComp}{" "}
            <div className={classes.controlBtnsContr}>
              <Button
                text={t("backToActivitiesList")}
                size="small"
                bold={true}
                onClick={onGoBack}
                customStyles={{
                  ButtonDefault: {
                    maxWidth: "max-content",
                    ..._.get(customStyles, "btnStyles", {}),
                  },
                }}
              />
              <Button
                text={t("newSearch")}
                size="small"
                bold={true}
                onClick={() => setShowActFormModal(true)}
                customStyles={{
                  ButtonDefault: {
                    maxWidth: "max-content",
                    ..._.get(customStyles, "btnStyles", {}),
                  },
                }}
              />
            </div>
          </div>
          <div className={classes.basicContent}>
            <Description details={activity} />
          </div>
          {activity.features.length > 0 && (
            <Info fieldName="features" data={activity} />
          )}
          {additionalInfo.length > 0 && (
            <Info fieldName="additionalInformation" data={additionalInfo} />
          )}
          {additionalInfo.length > 0 && (
            <Info
              fieldName="doThisBecause"
              data={_.get(activity, "highlights", [])}
            />
          )}
          {_.get(activity, "safety_info", []).length > 0 && (
            <Info
              fieldName="safetyInfo"
              data={_.get(activity, "safety_info", [])}
            />
          )}
          {_.get(activity, "inclusions.length", 0) > 0 && (
            <InclusionsExclusions />
          )}
          {activity.pickup_point_required &&
            activity.pickup_points.length > 0 && (
              <Info
                fieldName="pickupPoints"
                data={activity.pickup_points.map((pickup) => pickup.name)}
              />
            )}
          {activity.info && (
            <Info
              fieldName={t("whatToRemember")}
              data={[
                sanitizeHtml(activity.info, {
                  allowedTags: [],
                }),
              ]}
            />
          )}
          {activity.provider && (
            <Info
              fieldName="bookingFee"
              data={["Good news! No extra fees are applied to this booking."]}
              showBullet={false}
            />
          )}
          {activity.free_cancellation && (
            <Info
              fieldName={t("cancellationPolicy")}
              data={[
                "Receive a 100% refund if you cancel up to 24 hours before the experience begins.",
              ]}
              showBullet={false}
            />
          )}
          <div className={classes.mobile}>
            <ActPriceSearchForm
              price={activity?.price ?? {}}
              onCancel={() => onSetShowForm(false)}
              showForm={showForm}
              activity={activity}
              onSetShowModal={onSetShowModal}
              excludeTag={""}
              pickUpAndDates={pickUpAndDates}
              setPickUpAndDates={setPickUpAndDates}
              setBookingData={setBookingData}
            />
          </div>
          <div className={classes.desktop}>
            <ActPriceSearchForm
              price={activity?.price ?? {}}
              onCancel={() => onSetShowForm(false)}
              showForm={true}
              activity={activity}
              onSetShowModal={onSetShowModal}
              excludeTag={""}
              pickUpAndDates={pickUpAndDates}
              setPickUpAndDates={setPickUpAndDates}
              setBookingData={setBookingData}
            />
          </div>
          <ReviewsDetails />
        </React.Fragment>
      )}
      {!showForm && !loading && (
        <div className={classes.bookBtnContainer}>
          <Button
            text={t("bookNow")}
            onClick={() => {
              onSetShowForm(true);
              sendPixelEvent("AddToCart", {
                data: {
                  product_reference: _.get(activity, "title", ""),
                  product_type: "activity",
                },
              });
            }}
          />
        </div>
      )}
      {showModal && (
        <SchedulesModal
          activity_uid={activity.uid}
          page_session_id={page_session_id}
          bookingData={bookingData}
          modalLoading={modalLoading}
          setModalLoading={setModalLoading}
          excludeTag={""}
          pickUpAndDates={pickUpAndDates}
          onSetShowModal={onSetShowModal}
        />
      )}
    </div>
  );
};
ActivityDetails.propTypes = { page_session_id: PropTypes.string.isRequired };
ActivityDetails = withRouter(ActivityDetails);
export default ActivityDetails;
